/**
 * SectionRenderer — renders a single top-level section and its subsections.
 * Handles appears_when at both the section and subsection level.
 * Uses the existing Section component from fields.tsx for consistent styling.
 */

import { useFormContext } from 'react-hook-form';
import { Section } from '../../components/intake/fields';
import type { FieldDef, SectionDef, SubsectionDef, RendererProps } from './types';
import { FieldRenderer } from './FieldRenderer';
import { ConditionalR, flattenValues } from './containers/ConditionalR';
import { parseExpression } from './expression/parser';
import { evaluate } from './expression/evaluator';

/** True when a field's own appears_when (if any) currently evaluates to visible. */
export function isFieldGateOpen(field: FieldDef, flatValues: Record<string, unknown>): boolean {
  const expr = field.appears_when ?? field.conditional_on;
  if (!expr) return true;
  const ast = parseExpression(expr);
  if (!ast) return true;
  return evaluate(ast, flatValues);
}

/**
 * True when at least one field in a subsection currently renders (its own
 * appears_when, if any, is open). Drives the empty-subsection suppression
 * fix below: a subsection whose fields ALL fail to render must not show its
 * heading over an empty body (the "Critical data confirmation" orphan
 * heading class, 2026-07-13 fix, R4).
 */
export function subsectionHasVisibleField(
  fields: FieldDef[],
  flatValues: Record<string, unknown>,
): boolean {
  return fields.some((field) => isFieldGateOpen(field, flatValues));
}

interface Props extends RendererProps {
  section: SectionDef;
  /**
   * RC-3 (820 sweep D-02/D-06): the intake form renders one section at a time
   * under its own page heading, so this component's heading was a second copy
   * of the same title and description directly beneath the first. Pass false
   * there. Surfaces that render a whole form (PDF, preview, review) need the
   * per-section heading and keep the default.
   */
  showSectionHeading?: boolean;
}

export function SectionRenderer({
  section,
  formId,
  pdfMode = false,
  namePrefix,
  audience,
  showSectionHeading = true,
}: Props) {
  const sectionContent = (
    <Section
      title={showSectionHeading ? section.title : undefined}
      description={showSectionHeading ? section.description : undefined}
    >
      {/* Top-level fields on the section (no subsections) */}
      {(section.fields ?? []).map((field) => (
        <FieldRenderer
          key={field.canonical_name}
          field={field}
          formId={formId}
          pdfMode={pdfMode}
          namePrefix={namePrefix}
          audience={audience}
        />
      ))}

      {/* Subsections */}
      {(section.subsections ?? []).map((sub) => (
        <SubsectionRenderer
          key={sub.subsection_id}
          subsection={sub}
          formId={formId}
          pdfMode={pdfMode}
          namePrefix={namePrefix}
          audience={audience}
        />
      ))}
    </Section>
  );

  // Wrap in conditional if section has appears_when
  if (section.appears_when) {
    return (
      <ConditionalR expression={section.appears_when}>
        {sectionContent}
      </ConditionalR>
    );
  }

  return sectionContent;
}

// ---------------------------------------------------------------------------
// Subsection renderer
// ---------------------------------------------------------------------------

interface SubsectionProps extends RendererProps {
  subsection: SubsectionDef;
}

function SubsectionRenderer({ subsection, formId, pdfMode = false, namePrefix, audience }: SubsectionProps) {
  // Suppress the heading when every field in the subsection fails to render
  // (each field's own appears_when currently evaluates hidden) — otherwise an
  // orphan heading appears over an empty body, e.g. 309/300's "Critical data
  // confirmation" once all its fields' gates close (2026-07-13 fix, R4).
  // Fields with no appears_when always count as visible, so a normal
  // (ungated) subsection is never affected by this check.
  const { watch } = useFormContext();
  const flatValues = flattenValues(watch() as Record<string, unknown>);
  const hasVisibleField = subsectionHasVisibleField(subsection.fields, flatValues);

  if (!hasVisibleField) return null;

  const content = (
    <div className="space-y-4">
      {subsection.title && (
        <div>
          <h4 className="text-sm font-semibold text-slate-700">{subsection.title}</h4>
          {subsection.description && (
            <p className="text-xs text-slate-500 mt-0.5">{subsection.description}</p>
          )}
        </div>
      )}
      <div className="space-y-4">
        {subsection.fields.map((field) => (
          <FieldRenderer
            key={field.canonical_name}
            field={field}
            formId={formId}
            pdfMode={pdfMode}
            namePrefix={namePrefix}
            audience={audience}
          />
        ))}
      </div>
    </div>
  );

  if (subsection.appears_when) {
    return (
      <ConditionalR expression={subsection.appears_when}>
        {content}
      </ConditionalR>
    );
  }

  return content;
}
